/**
 * QueryErrorState — reusable «query failed» panel with a retry button.
 *
 * Phase-4 FIX-PACK FT.D2 (closes UI-MF1, UI-MF4). Mirrors the inline
 * error layout of `ProjectHistoryPanel` so list pages and tab bodies
 * surface a consistent recovery affordance instead of either:
 *
 *   - rendering the empty-state on a 5xx (false-empty), OR
 *   - relying on the global apiFetch toast to be the user's only signal.
 *
 * A11y:
 *   - `role="alert"` so SR users hear the failure without polling.
 *   - Retry button is a real `<button>`; pressing it should call
 *     `query.refetch()` (or any equivalent re-fetch handler).
 */
import * as React from 'react';
import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export interface QueryErrorStateProps {
  /** Bold headline. Defaults to «Не удалось загрузить данные». */
  headline?: string;
  /** Muted sub-copy. */
  description?: string;
  /** Retry handler — typically `() => query.refetch()`. */
  onRetry?: () => void;
  /** Override the retry button label. Defaults to «Повторить». */
  retryLabel?: string;
  /** Optional `data-testid` for end-to-end selectors. */
  testId?: string;
  className?: string;
}

const DEFAULT_HEADLINE = 'Не удалось загрузить данные';
const DEFAULT_RETRY_LABEL = 'Повторить';

export function QueryErrorState({
  headline = DEFAULT_HEADLINE,
  description,
  onRetry,
  retryLabel = DEFAULT_RETRY_LABEL,
  testId = 'query-error-state',
  className,
}: QueryErrorStateProps): React.ReactElement {
  return (
    <div
      role="alert"
      data-testid={testId}
      className={cn(
        'flex flex-col items-start gap-2 rounded-sm border border-line bg-surface-2 p-4 text-sm text-muted',
        className,
      )}
    >
      <p className="text-ink">{headline}</p>
      {description ? <p className="text-xs text-muted">{description}</p> : null}
      {onRetry ? (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={onRetry}
          data-testid={`${testId}-retry`}
        >
          <RefreshCw className="mr-1 size-3.5" aria-hidden="true" />
          {retryLabel}
        </Button>
      ) : null}
    </div>
  );
}
